import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  Modal,
  Input,
  Button,
  Tag,
  Card,
  Typography,
  Divider,
  Tooltip,
  Select,
  Skeleton,
  Badge,
  Space,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  SettingOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import "./AttributeFieldConfig.css";
import { useSelector, useDispatch } from "react-redux";
import {
  setChartEditField,
  setPolificOrderByAttribute,
  setX_attribute,
} from "../../../../redux/actions/WidgetMaker/GraphConfig";
import EditFieldModal from "../../EditFieldModal/EditFieldModal";
import { setFilterActiveKey } from "../../../../redux/actions/Filter";
import {
  setChartAttributesFieldConfig,
  setIsshowSideBar,
  setWidgetMakerApiCallFlag,
} from "../../../../redux/actions/WidgetMaker/WidgetMaker";
import informationButton from "../../../../assets/information-button.svg";
import { setWidgetMakerErrorCode } from "../../../../redux/actions/WidgetMaker/WidgetAPI";

const { Title, Text } = Typography;
const { Option } = Select;

// Constants
const CHART_LIMITS = {
  Y_SERIES: {
    grid: 7,
    default: 10,
  },
  Y_AXES: {
    grid: 2,
    default: 3,
  },
  QUICK_FILTER: 2,
};

const FIELD_ORDER = {
  x_axis: 1,
  y_series: 2,
  breakdown: 100,
  multilevel_breakdown: 101,
  y_axes: 100,
  filter_dropdown: 200,
};

const DEFAULT_AGGREGATION = "COUNT";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_LIMIT = 10;

const AttributeFieldConfig = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const authParams = useSelector((state) => state.Authentication.authParams);
  const axisPanelAtt = useSelector(
    (state) => state?.GraphConfig?.widgetGraphConfig || {}
  );
  const All_WM_attributes = useSelector(
    (state) => state.WidgetMaker.All_WM_attributes
  );
  const isshowSideBar = useSelector((state) => state.WidgetMaker.isshowSideBar);
  const graphInfoLoader = useSelector(
    (state) => state?.WidgetMaker?.graph_info_loader
  );
  const chartAttributesFieldConfig = useSelector(
    (state) => state?.WidgetMaker?.chart_attributes_field_config
  );

  // State management
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [currentField, setCurrentField] = useState("x_axis");
  const [ySeriesCount, setYSeriesCount] = useState(1);
  const [yAxesCount, setYAxesCount] = useState(1);
  const [quickFilterCount, setQuickFilterCount] = useState(0);
  const [newlyAddedField, setNewlyAddedField] = useState(null);

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFilterApplied, setisFilterApplied] = useState(false);
  const [filterSearch, setfilterSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState(null);
  const [editName, setEditName] = useState();
  const [editAggregation, setAggregation] = useState("count");
  const [widgetMakerClearFilter, setWidgetMakerClearFilter] = useState(false);

  // Prolific specific
  const [polificOptions, setPolificOptions] = useState("engagement");

  // Refs
  const highlightTimeoutRef = useRef(null);

  // Computed values
  const ySeriesLimit = useMemo(() => {
    const chartType = axisPanelAtt?.chart_type;
    return CHART_LIMITS.Y_SERIES[chartType] || CHART_LIMITS.Y_SERIES.default;
  }, [axisPanelAtt?.chart_type]);

  const yAxesLimit = useMemo(() => {
    const chartType = axisPanelAtt?.chart_type;
    return CHART_LIMITS.Y_AXES[chartType] || CHART_LIMITS.Y_AXES.default;
  }, [axisPanelAtt?.chart_type]);

  const dropdownClassName = useMemo(
    () => (authParams?.theme_type === "1" ? "darkTheme" : "lightTheme"),
    [authParams?.theme_type]
  );

  const fieldsData = chartAttributesFieldConfig?.data || [];

  const sortedFields = useMemo(() => {
    let fields = [...fieldsData];

    // Add multilevel_breakdown field if not present
    const hasMultilevelBreakdown = fields.some(
      (field) => field.payload_key_name === "multilevel_breakdown"
    );

    if (!hasMultilevelBreakdown) {
      // Check if we should show multilevel_breakdown for this chart type
      const chartTypesWithMultilevelBreakdown = ["cluster"];

      if (
        chartTypesWithMultilevelBreakdown.includes(axisPanelAtt?.chart_type)
      ) {
        fields.push({
          payload_key_name: "multilevel_breakdown",
          field_name: "Breakdown By",
          data_type: ["varchar"],
          multiple_allowed: false,
          attributes:
            fieldsData.find((f) => f.payload_key_name === "x_axis")
              ?.attributes || [],
        });
      }
    }

    return fields.sort((a, b) => {
      let orderA = FIELD_ORDER[a.payload_key_name] || 50;
      let orderB = FIELD_ORDER[b.payload_key_name] || 50;

      if (a.field_name.toLowerCase() === "values") orderA = 2;
      if (b.field_name.toLowerCase() === "values") orderB = 2;

      return orderA - orderB;
    });
  }, [fieldsData, axisPanelAtt?.chart_type]);

  // Helper function to determine if an attribute is a date/time type
  const isDateTimeAttribute = useCallback(
    (attributeName) => {
      return (
        axisPanelAtt?.graphConditions?.["date-attributes"]?.includes(
          attributeName
        ) || false
      );
    },
    [axisPanelAtt?.graphConditions]
  );

  // Helper function to determine if order_by should be reset for Grid/Pivot-Grid charts
  const shouldResetOrderByOnRemoval = useCallback(
    (fieldType, axisPanelAttData, chartTypeData) => {
      const isGridChart = chartTypeData === "grid";
      const isPivotGridChart = chartTypeData === "pivot-grid";
      const currentOrderBy = axisPanelAttData?.order_by;

      if (!isGridChart && !isPivotGridChart) {
        return false; // No reset logic for non-grid charts
      }

      if (!currentOrderBy?.attribute) {
        return false; // No current order_by to reset
      }

      // Check if the attribute being removed is the same as the current order_by attribute
      switch (fieldType) {
        case "x_axis":
          return (
            axisPanelAttData?.x_axis?.attribute === currentOrderBy.attribute
          );

        case "y_series":
        case "y_series_1":
        case "y_series_2":
        case "y_series_3":
        case "y_series_4":
        case "y_series_5":
        case "y_series_6":
        case "y_series_7":
          const ySeriesAttribute =
            axisPanelAttData?.y_axis?.[fieldType]?.attribute;
          return ySeriesAttribute === currentOrderBy.attribute;

        case "breakdown":
        case "y_axes":
          return axisPanelAttData?.breakdown_by?.some(
            (breakdown) => breakdown.attribute === currentOrderBy.attribute
          );

        default:
          if (fieldType.startsWith("y_axes_")) {
            const axesNumber = parseInt(fieldType.split("_")[2]);
            return (
              axisPanelAttData?.breakdown_by?.[axesNumber - 1]?.attribute ===
              currentOrderBy.attribute
            );
          }
          if (fieldType.startsWith("y_series_")) {
            const ySeriesAttribute =
              axisPanelAttData?.y_axis?.[fieldType]?.attribute;
            return ySeriesAttribute === currentOrderBy.attribute;
          }
          return false;
      }
    },
    []
  );

  // Helper function to determine new order_by when current one needs to be reset
  const determineNewOrderBy = useCallback(
    (
      fieldType,
      axisPanelAttData,
      chartTypeData,
      removedAttributeName = null
    ) => {
      const isGridChart = chartTypeData === "grid";
      const isPivotGridChart = chartTypeData === "pivot-grid";

      if (!isGridChart && !isPivotGridChart) {
        return null; // No order_by logic for non-grid charts
      }

      switch (fieldType) {
        case "x_axis":
          // When x_axis is removed, order by y_series_1 if it exists
          if (axisPanelAttData?.y_axis?.y_series_1?.attribute) {
            return {
              attribute: axisPanelAttData.y_axis.y_series_1.attribute,
              data_source: axisPanelAttData.y_axis.y_series_1.data_source,
              sort_order: "desc",
              no_of_rows: 10,
              offset: 0,
            };
          }
          break;

        case "y_series":
        case "y_series_1":
          // When y_series_1 is removed, check if there are other y_series
          const remainingYSeries = Object.keys(axisPanelAttData?.y_axis || {})
            .filter((key) => key.startsWith("y_series_") && key !== fieldType)
            .map((key) => axisPanelAttData.y_axis[key])
            .filter((series) => series?.attribute);

          if (remainingYSeries.length > 0) {
            // Order by the first remaining y_series
            return {
              attribute: remainingYSeries[0].attribute,
              data_source: remainingYSeries[0].data_source,
              sort_order: "desc",
              no_of_rows: 10,
              offset: 0,
            };
          } else if (axisPanelAttData?.x_axis?.attribute) {
            // Fallback to x_axis if no y_series remain
            return {
              attribute: axisPanelAttData.x_axis.attribute,
              data_source: axisPanelAttData.x_axis.data_source,
              sort_order: "desc",
              no_of_rows: 10,
              offset: 0,
            };
          }
          break;

        case "breakdown":
        case "y_axes":
          // When breakdown is removed, check remaining breakdowns first
          const remainingBreakdowns = (
            axisPanelAttData?.breakdown_by || []
          ).filter((breakdown) => breakdown.attribute !== removedAttributeName);

          if (remainingBreakdowns.length > 0) {
            // Order by the first remaining breakdown
            return {
              attribute: remainingBreakdowns[0].attribute,
              data_source: remainingBreakdowns[0].data_source,
              sort_order: "desc",
              no_of_rows: 10,
              offset: 0,
            };
          } else if (axisPanelAttData?.y_axis?.y_series_1?.attribute) {
            // Fallback to y_series_1
            return {
              attribute: axisPanelAttData.y_axis.y_series_1.attribute,
              data_source: axisPanelAttData.y_axis.y_series_1.data_source,
              sort_order: "desc",
              no_of_rows: 10,
              offset: 0,
            };
          } else if (axisPanelAttData?.x_axis?.attribute) {
            // Fallback to x_axis
            return {
              attribute: axisPanelAttData.x_axis.attribute,
              data_source: axisPanelAttData.x_axis.data_source,
              sort_order: "desc",
              no_of_rows: 10,
              offset: 0,
            };
          }
          break;

        default:
          if (fieldType.startsWith("y_series_")) {
            // Handle numbered y_series removal
            const remainingYSeries = Object.keys(axisPanelAttData?.y_axis || {})
              .filter((key) => key.startsWith("y_series_") && key !== fieldType)
              .map((key) => axisPanelAttData.y_axis[key])
              .filter((series) => series?.attribute);

            if (remainingYSeries.length > 0) {
              return {
                attribute: remainingYSeries[0].attribute,
                data_source: remainingYSeries[0].data_source,
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            }
          } else if (fieldType.startsWith("y_axes_")) {
            // Handle numbered y_axes removal
            const axesNumber = parseInt(fieldType.split("_")[2]);
            const remainingBreakdowns = (
              axisPanelAttData?.breakdown_by || []
            ).filter((_, index) => index !== axesNumber - 1);

            if (remainingBreakdowns.length > 0) {
              return {
                attribute: remainingBreakdowns[0].attribute,
                data_source: remainingBreakdowns[0].data_source,
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            }
          }
          break;
      }

      // If no suitable replacement found, return null to clear order_by
      return null;
    },
    []
  );

  // Helper functions
  const createAttributeData = useCallback(
    (attribute, traceName) => ({
      attribute,
      title: All_WM_attributes?.[attribute] || traceName || attribute,
      datatype: "varchar",
      source: "attribute",
    }),
    [All_WM_attributes]
  );

  const highlightField = useCallback((fieldKey) => {
    setNewlyAddedField(fieldKey);

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    highlightTimeoutRef.current = setTimeout(() => {
      setNewlyAddedField(null);
    }, 3000);
  }, []);

  // Effects
  useEffect(() => {
    const field = chartAttributesFieldConfig?.selected_field || "x_axis";
    setCurrentField(field);
  }, [chartAttributesFieldConfig?.selected_field]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Reset yAxesCount when chart type changes
  useEffect(() => {
    // For cluster-chart and grid, don't reset count if user is adding more nodes
    const isClusterChart = axisPanelAtt?.chart_type === "cluster-chart";
    const isGridChart = axisPanelAtt?.chart_type === "grid";

    // Calculate the correct yAxesCount based on existing breakdown_by data
    if (
      axisPanelAtt?.breakdown_by &&
      Array.isArray(axisPanelAtt.breakdown_by)
    ) {
      // Count only breakdown items that have actual attribute values
      const actualBreakdownCount = axisPanelAtt.breakdown_by.filter(
        (item) => item?.attribute
      ).length;

      // For cluster-chart, grid, and sankey, keep the higher count to allow adding more nodes
      const isSankeyChart = axisPanelAtt?.chart_type === "sankey";
      if (isClusterChart || isGridChart || isSankeyChart) {
        setYAxesCount((prevCount) =>
          Math.max(prevCount, actualBreakdownCount, 1)
        );
      } else {
        // Set yAxesCount based on actual values, minimum 1
        setYAxesCount(Math.max(1, actualBreakdownCount));
      }
    } else {
      // No breakdown_by data, reset to 1
      setYAxesCount(1);
    }
  }, [axisPanelAtt?.chart_type]); // Trigger when chart type changes

  // Sync selected attributes with Redux
  useEffect(() => {
    const syncSelectedAttributesFromRedux = () => {
      const newSelectedAttributes = {};

      // Sync x_axis
      if (axisPanelAtt?.x_axis?.attribute) {
        newSelectedAttributes.x_axis = createAttributeData(
          axisPanelAtt.x_axis.attribute,
          axisPanelAtt.x_axis.trace_name
        );
      }

      // Sync multilevel_breakdown
      if (axisPanelAtt?.multilevel_breakdown?.attribute) {
        newSelectedAttributes.multilevel_breakdown = createAttributeData(
          axisPanelAtt.multilevel_breakdown.attribute,
          axisPanelAtt.multilevel_breakdown.trace_name
        );
      }

      // Sync breakdown_by
      if (axisPanelAtt?.breakdown_by?.length > 0) {
        axisPanelAtt.breakdown_by.forEach((breakdown, index) => {
          const fieldKey = `y_axes_${index + 1}`;
          newSelectedAttributes[fieldKey] = createAttributeData(
            breakdown.attribute,
            breakdown.trace_name
          );
        });

        // Sync first breakdown to regular breakdown field
        const firstBreakdown = axisPanelAtt.breakdown_by[0];
        if (firstBreakdown) {
          newSelectedAttributes.breakdown = createAttributeData(
            firstBreakdown.attribute,
            firstBreakdown.trace_name
          );
        }

        // Calculate the actual count of breakdown items with values
        const actualBreakdownCount = axisPanelAtt.breakdown_by.filter(
          (item) => item?.attribute
        ).length;

        // Update yAxesCount if needed
        // For cluster-chart and grid, don't reduce the count to allow adding more nodes
        const isClusterChart = axisPanelAtt?.chart_type === "cluster-chart";
        const isGridChart = axisPanelAtt?.chart_type === "grid";
        const isSankeyChart = axisPanelAtt?.chart_type === "sankey";
        if (isClusterChart || isGridChart || isSankeyChart) {
          // Only increase count, never decrease for cluster-chart, grid, and sankey
          if (actualBreakdownCount > yAxesCount) {
            setYAxesCount(actualBreakdownCount);
          }
        } else {
          // For other charts, sync the count
          if (yAxesCount !== actualBreakdownCount) {
            setYAxesCount(Math.max(1, actualBreakdownCount));
          }
        }
      } else {
        // No breakdown_by data, ensure at least 1
        if (yAxesCount !== 1) {
          setYAxesCount(1);
        }
      }

      // Sync y_axis (y_series)
      if (axisPanelAtt?.y_axis && typeof axisPanelAtt.y_axis === "object") {
        Object.entries(axisPanelAtt.y_axis).forEach(
          ([ySeriesKey, yAxisData]) => {
            if (yAxisData?.attribute) {
              newSelectedAttributes[ySeriesKey] = {
                ...createAttributeData(
                  yAxisData.attribute,
                  yAxisData.trace_name
                ),
                datatype: "int",
                source: "metrics",
              };
            }
          }
        );
      }

      // Sync order_by
      if (axisPanelAtt?.order_by?.attribute) {
        newSelectedAttributes.order_by = createAttributeData(
          axisPanelAtt.order_by.attribute,
          axisPanelAtt.order_by.trace_name
        );
      }

      // Sync multilevel_breakdown
      if (axisPanelAtt?.multilevel_breakdown?.attribute) {
        newSelectedAttributes.multilevel_breakdown = createAttributeData(
          axisPanelAtt.multilevel_breakdown.attribute,
          axisPanelAtt.multilevel_breakdown.trace_name
        );
      }

      // Sync filter_by
      if (
        axisPanelAtt?.filter_by?.length > 0 &&
        axisPanelAtt.filter_by[0]?.attribute
      ) {
        newSelectedAttributes.filter_by = createAttributeData(
          axisPanelAtt.filter_by[0].attribute,
          axisPanelAtt.filter_by[0].trace_name
        );
      }

      // Sync filter_dropdown
      if (axisPanelAtt?.filter_dropdown?.length > 0) {
        newSelectedAttributes.filter_dropdown =
          axisPanelAtt.filter_dropdown.map((filterData) =>
            createAttributeData(filterData.attribute, filterData.trace_name)
          );

        axisPanelAtt.filter_dropdown.forEach((filterData, index) => {
          const fieldKey = `filter_dropdown_${index + 1}`;
          newSelectedAttributes[fieldKey] = createAttributeData(
            filterData.attribute,
            filterData.trace_name
          );
        });

        if (quickFilterCount < axisPanelAtt.filter_dropdown.length) {
          setQuickFilterCount(axisPanelAtt.filter_dropdown.length);
        }
      }

      // Handle Y-series count
      const fieldsData = chartAttributesFieldConfig?.data || [];
      const ySeriesField = fieldsData.find(
        (field) =>
          field.payload_key_name === "y_series" ||
          field.field_name.toLowerCase() === "values"
      );
      const allowsMultiple = ySeriesField?.multiple_allowed || false;

      // For scatter and multilayer-map charts, always show 2 Y-series fields by default
      const chartType = axisPanelAtt?.chart_type;
      const isScatterOrMultiLayer =
        chartType === "scatter" || chartType === "multilayer-map";

      if (isScatterOrMultiLayer) {
        // For scatter and multilayer-map charts, ensure we have at least 2 Y-series fields
        const ySeriesKeys = Object.keys(axisPanelAtt?.y_axis || {}).filter(
          (key) => key.startsWith("y_series_")
        );
        const existingCount =
          ySeriesKeys.length > 0
            ? Math.max(
                ...ySeriesKeys.map((key) => parseInt(key.split("_")[2]) || 1)
              )
            : 0;
        setYSeriesCount(Math.max(2, existingCount));
      } else if (!allowsMultiple) {
        setYSeriesCount(1);
        Object.keys(newSelectedAttributes).forEach((key) => {
          if (key.startsWith("y_series_") && key !== "y_series_1") {
            delete newSelectedAttributes[key];
          }
        });
      } else if (axisPanelAtt?.y_axis) {
        const ySeriesKeys = Object.keys(axisPanelAtt.y_axis).filter((key) =>
          key.startsWith("y_series_")
        );
        if (ySeriesKeys.length > 0) {
          const maxSeriesNumber = Math.max(
            ...ySeriesKeys.map((key) => parseInt(key.split("_")[2]) || 1)
          );
          setYSeriesCount((prevCount) => Math.max(maxSeriesNumber, prevCount));
        }
      }

      setSelectedAttributes(newSelectedAttributes);
    };

    syncSelectedAttributesFromRedux();
  }, [
    axisPanelAtt,
    All_WM_attributes,
    createAttributeData,
    chartAttributesFieldConfig,
  ]);

  // Quick filter sync effect
  useEffect(() => {
    if (axisPanelAtt?.filter_dropdown?.length > 0) {
      const filterDataArray = axisPanelAtt.filter_dropdown.map((filterData) =>
        createAttributeData(filterData.attribute, filterData.trace_name)
      );

      const newAttrs = {
        filter_dropdown: filterDataArray,
      };

      axisPanelAtt.filter_dropdown.forEach((filterData, index) => {
        const fieldKey = `filter_dropdown_${index + 1}`;
        newAttrs[fieldKey] = createAttributeData(
          filterData.attribute,
          filterData.trace_name
        );
      });

      setSelectedAttributes((prev) => ({ ...prev, ...newAttrs }));

      if (quickFilterCount < axisPanelAtt.filter_dropdown.length) {
        setQuickFilterCount(axisPanelAtt.filter_dropdown.length);
      }
    } else {
      setSelectedAttributes((prev) => {
        const newAttrs = { ...prev };
        delete newAttrs.filter_dropdown;
        Object.keys(newAttrs).forEach((key) => {
          if (key.startsWith("filter_dropdown_")) {
            delete newAttrs[key];
          }
        });
        return newAttrs;
      });
    }
  }, [
    axisPanelAtt?.filter_dropdown,
    createAttributeData,
    quickFilterCount,
    chartAttributesFieldConfig,
  ]);

  // Initialize quick filter count
  useEffect(() => {
    if (axisPanelAtt?.filter_dropdown?.length > 0) {
      const currentCount = axisPanelAtt.filter_dropdown.length;
      if (quickFilterCount < currentCount) {
        setQuickFilterCount(currentCount);
      }
    }
  }, [
    axisPanelAtt?.filter_dropdown,
    quickFilterCount,
    chartAttributesFieldConfig,
  ]);

  // Action handlers
  const openAttributeModal = useCallback(
    (field) => {
      let selectedField = field;

      // Map y_series_N fields to base field name
      if (field.startsWith("y_series_")) {
        const ySeriesFieldConfig = chartAttributesFieldConfig?.data?.find(
          (f) =>
            f.payload_key_name === "y_series" ||
            f.field_name?.toLowerCase().includes("measure") ||
            f.field_name?.toLowerCase().includes("value")
        );
        if (ySeriesFieldConfig?.payload_key_name) {
          selectedField = ySeriesFieldConfig.payload_key_name;
        }
      }

      // Map filter_dropdown_N fields similarly
      if (field.startsWith("filter_dropdown_")) {
        const filterDropdownFieldConfig =
          chartAttributesFieldConfig?.data?.find(
            (f) => f.payload_key_name === "filter_dropdown"
          );
        if (filterDropdownFieldConfig?.payload_key_name) {
          selectedField = filterDropdownFieldConfig.payload_key_name;
        }
      }

      // dispatch(
      //   setX_attribute({
      //     ...axisPanelAtt,
      //     chart_attributes_field_config: {
      //       ...chartAttributesFieldConfig,
      //       data: chartAttributesFieldConfig?.data,
      //       selected_field: field,
      //       actual_field_clicked: field,
      //       open_chart_setting_flag: false,
      //     },
      //   })
      // );

      dispatch(
        setChartAttributesFieldConfig({
          ...chartAttributesFieldConfig,
          data: chartAttributesFieldConfig?.data,
          selected_field: field,
          actual_field_clicked: field,
          open_chart_setting_flag: false,
          selected_chart_type: chartAttributesFieldConfig?.selected_chart_type, // Preserve to prevent re-fetch
        })
      );

      if (isshowSideBar) {
        dispatch(setIsshowSideBar(false));
      }
    },
    [axisPanelAtt, dispatch, isshowSideBar, chartAttributesFieldConfig]
  );

  const removeAttribute = useCallback(
    (fieldType, attributeName = null) => {
      try {
        if (!chartAttributesFieldConfig.data) return;
        const chartType = axisPanelAtt?.chart_type;

        // For cluster-chart with deep_dive_on, reset all fields when removing any attribute
        const shouldResetDeepDive =
          chartType === "cluster-chart" && axisPanelAtt?.deep_dive_on;

        // Note: We'll handle deep_dive_on reset within each field type removal
        // This allows the normal field removal to proceed and trigger API calls

        // Find field config
        let fieldConfig;
        if (fieldType.startsWith("y_series_")) {
          if (axisPanelAtt?.chart_type === "kpi") {
            fieldConfig = chartAttributesFieldConfig.data.find(
              (field) => field.payload_key_name === "y_series"
            );
            dispatch(setWidgetMakerApiCallFlag(true));
            dispatch({ type: "SET_WIDGET_MAKER_CHART", payload: null });
            dispatch(setWidgetMakerErrorCode(null));
          } else {
            fieldConfig = chartAttributesFieldConfig.data.find(
              (field) => field.payload_key_name === "y_series"
            );
          }
        } else if (fieldType.startsWith("filter_dropdown_")) {
          fieldConfig = chartAttributesFieldConfig.data.find(
            (field) => field.payload_key_name === "filter_dropdown"
          );
        } else if (fieldType.startsWith("y_axes_")) {
          // Handle numbered y_axes fields
          fieldConfig = chartAttributesFieldConfig.data.find(
            (field) => field.payload_key_name === "y_axes"
          );
        } else {
          fieldConfig = chartAttributesFieldConfig.data.find(
            (field) => field.payload_key_name === fieldType
          );
        }

        if (!fieldConfig) return;

        const allowsMultiple = fieldConfig.multiple_allowed || false;

        if (allowsMultiple && attributeName) {
          setSelectedAttributes((prev) => ({
            ...prev,
            [fieldType]: (prev[fieldType] || []).filter(
              (attr) => attr.attribute !== attributeName
            ),
          }));
        } else {
          setSelectedAttributes((prev) => ({
            ...prev,
            [fieldType]: allowsMultiple ? [] : null,
          }));

          // Handle specific field types
          if (fieldType === "x_axis") {
            // Check if order_by should be reset for Grid/Pivot-Grid charts
            const shouldReset = shouldResetOrderByOnRemoval(
              fieldType,
              axisPanelAtt,
              chartType
            );
            const newOrderBy = shouldReset
              ? determineNewOrderBy("x_axis", axisPanelAtt, chartType)
              : axisPanelAtt?.order_by;
            dispatch(
              setX_attribute({
                ...axisPanelAtt,
                x_axis: null,
                ...(shouldReset ? { order_by: newOrderBy } : {}),
                ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
              })
            );
          } else if (fieldType.startsWith("y_series_")) {
            const currentYAxis = axisPanelAtt?.y_axis || {};
            const updatedYAxis = { ...currentYAxis };

            // Check if order_by should be reset for Grid/Pivot-Grid charts
            const shouldReset = shouldResetOrderByOnRemoval(
              fieldType,
              axisPanelAtt,
              chartType
            );
            const newOrderBy = shouldReset
              ? determineNewOrderBy(
                  fieldType,
                  axisPanelAtt,
                  chartType,
                  currentYAxis[fieldType]?.attribute
                )
              : axisPanelAtt?.order_by;

            updatedYAxis[fieldType] = {
              attribute: null,
              aggregation: updatedYAxis[fieldType]?.aggregation || null,
            };

            // For y_series_2 and higher, completely remove the field when clearing
            const seriesNumber = parseInt(fieldType.split("_")[2]);
            if (seriesNumber > 1) {
              // Remove the field entirely for y_series_2, y_series_3, etc.
              delete updatedYAxis[fieldType];
            } else {
              // For y_series_1, just clear the attribute
              updatedYAxis[fieldType] = {
                attribute: null,
                aggregation: updatedYAxis[fieldType]?.aggregation || null,
              };
            }

            // Trigger API call when removing y-series attribute
            dispatch(setWidgetMakerApiCallFlag(true));
            dispatch({ type: "SET_WIDGET_MAKER_CHART", payload: null });
            dispatch(setWidgetMakerErrorCode(null));

            dispatch(
              setX_attribute({
                ...axisPanelAtt,
                y_axis: updatedYAxis,
                ...(shouldReset ? { order_by: newOrderBy } : {}),
                ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
              })
            );
          } else if (
            fieldType === "breakdown" ||
            fieldType === "y_axes" ||
            fieldType.startsWith("y_axes_")
          ) {
            // Handle y_axes_1, y_axes_2, etc.
            if (fieldType.startsWith("y_axes_")) {
              const axesNumber = parseInt(fieldType.split("_")[2]);
              const currentBreakdownBy = axisPanelAtt?.breakdown_by || [];

              // Remove the item at the specific index
              const updatedBreakdownBy = currentBreakdownBy.filter(
                (_, index) => index !== axesNumber - 1
              );

              // Check if order_by should be reset for Grid/Pivot-Grid charts
              const shouldReset = shouldResetOrderByOnRemoval(
                "breakdown_by",
                axisPanelAtt,
                chartType
              );
              const newOrderBy = shouldReset
                ? determineNewOrderBy("breakdown_by", axisPanelAtt, chartType)
                : axisPanelAtt?.order_by;

              dispatch(
                setX_attribute({
                  ...axisPanelAtt,
                  breakdown_by: updatedBreakdownBy,
                  ...(shouldReset ? { order_by: newOrderBy } : {}),
                  ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
                })
              );

              // Update local state to remove the field
              setSelectedAttributes((prev) => {
                const newAttrs = { ...prev };
                delete newAttrs[fieldType];
                return newAttrs;
              });
            } else {
              // Handle generic breakdown/y_axes field
              const fieldsData = chartAttributesFieldConfig?.data || [];
              const yAxesField = fieldsData.find(
                (field) => field.payload_key_name === "y_axes"
              );
              const isMultiple = yAxesField?.multiple_allowed || false;

              if (isMultiple && attributeName) {
                const updatedBreakdownBy = (
                  axisPanelAtt?.breakdown_by || []
                ).filter((item) => item.attribute !== attributeName);

                // Check if order_by should be reset for Grid/Pivot-Grid charts
                const shouldReset = shouldResetOrderByOnRemoval(
                  "breakdown_by",
                  axisPanelAtt,
                  chartType
                );
                const newOrderBy = shouldReset
                  ? determineNewOrderBy(
                      "breakdown_by",
                      axisPanelAtt,
                      chartType,
                      attributeName
                    )
                  : axisPanelAtt?.order_by;

                dispatch(
                  setX_attribute({
                    ...axisPanelAtt,
                    breakdown_by: updatedBreakdownBy,
                    ...(shouldReset ? { order_by: newOrderBy } : {}),
                    ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
                  })
                );
              } else {
                // Check if order_by should be reset for Grid/Pivot-Grid charts
                const shouldReset = shouldResetOrderByOnRemoval(
                  "breakdown_by",
                  axisPanelAtt,
                  chartType
                );
                const newOrderBy = shouldReset
                  ? determineNewOrderBy("breakdown_by", axisPanelAtt, chartType)
                  : axisPanelAtt?.order_by;

                dispatch(
                  setX_attribute({
                    ...axisPanelAtt,
                    breakdown_by: [],
                    ...(shouldReset ? { order_by: newOrderBy } : {}),
                    ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
                  })
                );
              }
            }
          } else if (fieldType === "order_by") {
            dispatch(
              setX_attribute({
                ...axisPanelAtt,
                order_by: null,
                ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
              })
            );
          } else if (fieldType === "multilevel_breakdown") {
            dispatch(
              setX_attribute({
                ...axisPanelAtt,
                multilevel_breakdown: null,
                ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
              })
            );
          } else if (fieldType === "filter_by") {
            dispatch(
              setX_attribute({
                ...axisPanelAtt,
                filter_by: [],
                breakdown_by: [],
                postcard_pills_filter: null,
                filter_by_data: null,
                ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
              })
            );
          } else if (
            fieldType === "filter_dropdown" ||
            fieldType.startsWith("filter_dropdown_")
          ) {
            if (fieldType.startsWith("filter_dropdown_")) {
              const filterNumber = parseInt(fieldType.split("_")[2]);
              const filterIndex = filterNumber - 1;

              if (axisPanelAtt?.filter_dropdown) {
                const updatedFilterDropdown = [...axisPanelAtt.filter_dropdown];
                if (filterIndex < updatedFilterDropdown.length) {
                  updatedFilterDropdown.splice(filterIndex, 1);
                }
                dispatch(
                  setX_attribute({
                    ...axisPanelAtt,
                    filter_dropdown: updatedFilterDropdown,
                    filter_by_dropdown: [],
                    filter_by_dropdown_data: [],
                    ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
                  })
                );

                if (updatedFilterDropdown.length < quickFilterCount) {
                  setQuickFilterCount(
                    Math.max(1, updatedFilterDropdown.length)
                  );
                }
              }
            } else {
              dispatch(
                setX_attribute({
                  ...axisPanelAtt,
                  filter_dropdown: [],
                  filter_by_dropdown: [],
                  filter_by_dropdown_data: [],
                  ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
                })
              );
            }
          }
        }
      } catch (error) {
        console.error("Error removing attribute:", error);
        setSelectedAttributes((prev) => ({
          ...prev,
          [fieldType]: null,
        }));
      }
    },
    [axisPanelAtt, dispatch, quickFilterCount, chartAttributesFieldConfig]
  );

  const addYSeries = useCallback(() => {
    if (ySeriesCount < ySeriesLimit) {
      const newSeriesNumber = ySeriesCount + 1;
      setYSeriesCount(newSeriesNumber);

      const newFieldKey = `y_series_${newSeriesNumber}`;
      highlightField(newFieldKey);

      setTimeout(() => {
        openAttributeModal(newFieldKey);
      }, 100);
    }
  }, [ySeriesCount, ySeriesLimit, highlightField, openAttributeModal]);

  const addYAxes = useCallback(() => {
    if (yAxesCount < yAxesLimit) {
      const newAxesNumber = yAxesCount + 1;
      setYAxesCount(newAxesNumber);

      const newFieldKey = `y_axes_${newAxesNumber}`;
      highlightField(newFieldKey);

      setTimeout(() => {
        openAttributeModal(newFieldKey);
      }, 100);
    }
  }, [yAxesCount, yAxesLimit, highlightField, openAttributeModal]);

  const addQuickFilter = useCallback(() => {
    const currentCount = Math.max(1, quickFilterCount);
    if (currentCount < CHART_LIMITS.QUICK_FILTER) {
      const newFilterNumber = currentCount + 1;
      setQuickFilterCount(newFilterNumber);

      const newFieldKey = `filter_dropdown_${newFilterNumber}`;
      highlightField(newFieldKey);

      setTimeout(() => {
        openAttributeModal(newFieldKey);
      }, 100);
    }
  }, [quickFilterCount, highlightField, openAttributeModal]);

  const removeQuickFilter = useCallback(
    (filterNumber) => {
      try {
        if (filterNumber > 1) {
          const fieldKey = `filter_dropdown_${filterNumber}`;
          setSelectedAttributes((prev) => {
            const newAttrs = { ...prev };
            delete newAttrs[fieldKey];

            // Renumber remaining filters
            for (let i = filterNumber + 1; i <= quickFilterCount; i++) {
              const oldKey = `filter_dropdown_${i}`;
              const newKey = `filter_dropdown_${i - 1}`;
              if (newAttrs[oldKey]) {
                newAttrs[newKey] = newAttrs[oldKey];
                delete newAttrs[oldKey];
              }
            }

            return newAttrs;
          });

          // Update Redux
          if (axisPanelAtt?.filter_dropdown) {
            const updatedFilterDropdown = [...axisPanelAtt.filter_dropdown];
            if (filterNumber - 1 < updatedFilterDropdown.length) {
              updatedFilterDropdown.splice(filterNumber - 1, 1);
            }
            // Check if deep_dive_on should be reset for cluster-chart
            const chartType = axisPanelAtt?.chart_type;
            const shouldResetDeepDive =
              chartType === "cluster-chart" && axisPanelAtt?.deep_dive_on;

            dispatch(
              setX_attribute({
                ...axisPanelAtt,
                filter_dropdown: updatedFilterDropdown,
                filter_by_dropdown: [],
                filter_by_dropdown_data: [],
                ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
              })
            );
          }

          // Always decrement the count when removing any filter
          setQuickFilterCount(quickFilterCount - 1);

          // Auto-select the previous field (Quick Filter field before the removed one)
          const previousFieldKey =
            filterNumber > 2
              ? `filter_dropdown_${filterNumber - 1}`
              : "filter_dropdown_1";
          dispatch(
            setChartAttributesFieldConfig({
              ...chartAttributesFieldConfig,
              selected_field: previousFieldKey,
              actual_field_clicked: previousFieldKey,
            })
          );
        }
      } catch (error) {
        console.error("Error removing Quick Filter field:", error);
      }
    },
    [axisPanelAtt, dispatch, quickFilterCount]
  );

  const removeYAxes = useCallback(
    (axesNumber) => {
      try {
        if (axesNumber > 1) {
          const fieldKey = `y_axes_${axesNumber}`;
          const chartType = axisPanelAtt?.chart_type;

          // Update selected attributes - renumber all fields after the removed one
          setSelectedAttributes((prev) => {
            const newAttrs = { ...prev };
            delete newAttrs[fieldKey];

            // Renumber subsequent y_axes fields
            for (let i = axesNumber + 1; i <= yAxesCount; i++) {
              const oldKey = `y_axes_${i}`;
              const newKey = `y_axes_${i - 1}`;
              if (newAttrs[oldKey]) {
                newAttrs[newKey] = newAttrs[oldKey];
                delete newAttrs[oldKey];
              }
            }

            return newAttrs;
          });

          const currentBreakdownBy = axisPanelAtt?.breakdown_by || [];
          const removedAttribute =
            currentBreakdownBy[axesNumber - 1]?.attribute;
          const updatedBreakdownBy = currentBreakdownBy.filter(
            (_, index) => index !== axesNumber - 1
          );

          // Check if order_by should be reset for Grid/Pivot-Grid charts
          const shouldReset = shouldResetOrderByOnRemoval(
            fieldKey,
            axisPanelAtt,
            chartType
          );
          const newOrderBy = shouldReset
            ? determineNewOrderBy(
                fieldKey,
                axisPanelAtt,
                chartType,
                removedAttribute
              )
            : axisPanelAtt?.order_by;

          // Check if deep_dive_on should be reset for cluster-chart
          const shouldResetDeepDive =
            chartType === "cluster-chart" && axisPanelAtt?.deep_dive_on;

          dispatch(
            setX_attribute({
              ...axisPanelAtt,
              breakdown_by: updatedBreakdownBy,
              ...(shouldReset ? { order_by: newOrderBy } : {}),
              ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
            })
          );

          // Always decrement the count when removing any y_axes
          setYAxesCount(Math.max(1, yAxesCount - 1));

          // Auto-select the previous field (Group By field before the removed one)
          const previousFieldKey =
            axesNumber > 2 ? `y_axes_${axesNumber - 1}` : "y_axes_1";
          dispatch(
            setChartAttributesFieldConfig({
              ...chartAttributesFieldConfig,
              selected_field: previousFieldKey,
              actual_field_clicked: previousFieldKey,
            })
          );
        }
      } catch (error) {
        console.error("Error removing Group By field:", error);
      }
    },
    [
      axisPanelAtt,
      dispatch,
      yAxesCount,
      shouldResetOrderByOnRemoval,
      determineNewOrderBy,
    ]
  );

  const removeYSeries = useCallback(
    (seriesNumber) => {
      try {
        if (seriesNumber > 1) {
          const fieldKey = `y_series_${seriesNumber}`;
          const chartType = axisPanelAtt?.chart_type;

          // Update selected attributes - renumber all fields after the removed one
          setSelectedAttributes((prev) => {
            const newAttrs = { ...prev };
            delete newAttrs[fieldKey];

            // Renumber subsequent y_series fields
            for (let i = seriesNumber + 1; i <= ySeriesCount; i++) {
              const oldKey = `y_series_${i}`;
              const newKey = `y_series_${i - 1}`;
              if (newAttrs[oldKey]) {
                newAttrs[newKey] = newAttrs[oldKey];
                delete newAttrs[oldKey];
              }
            }

            return newAttrs;
          });

          const currentYAxis = axisPanelAtt?.y_axis || {};
          const updatedYAxis = { ...currentYAxis };
          delete updatedYAxis[fieldKey];

          // Renumber subsequent y_series in y_axis
          for (let i = seriesNumber + 1; i <= ySeriesCount; i++) {
            const oldKey = `y_series_${i}`;
            const newKey = `y_series_${i - 1}`;
            if (updatedYAxis[oldKey]) {
              updatedYAxis[newKey] = updatedYAxis[oldKey];
              delete updatedYAxis[oldKey];
            }
          }

          const hasKeys =
            updatedYAxis &&
            typeof updatedYAxis === "object" &&
            Object.keys(updatedYAxis).length > 0;

          // Check if order_by should be reset for Grid/Pivot-Grid charts
          const shouldReset = shouldResetOrderByOnRemoval(
            fieldKey,
            axisPanelAtt,
            chartType
          );
          const newOrderBy = shouldReset
            ? determineNewOrderBy(
                fieldKey,
                axisPanelAtt,
                chartType,
                axisPanelAtt?.y_axis?.[fieldKey]?.attribute
              )
            : axisPanelAtt?.order_by;

          // Check if deep_dive_on should be reset for cluster-chart
          const shouldResetDeepDive =
            chartType === "cluster-chart" && axisPanelAtt?.deep_dive_on;

          dispatch(
            setX_attribute({
              ...axisPanelAtt,
              y_axis: hasKeys ? updatedYAxis : null,
              ...(shouldReset ? { order_by: newOrderBy } : {}),
              ...(shouldResetDeepDive ? { deep_dive_on: null } : {}),
            })
          );

          // Always decrement the count when removing any y_series
          setYSeriesCount(ySeriesCount - 1);

          // Auto-select the previous field (Y-series field before the removed one)
          const previousFieldKey =
            seriesNumber > 2 ? `y_series_${seriesNumber - 1}` : "y_series_1";
          dispatch(
            setChartAttributesFieldConfig({
              ...chartAttributesFieldConfig,
              selected_field: previousFieldKey,
              actual_field_clicked: previousFieldKey,
            })
          );
        }
      } catch (error) {
        console.error("Error removing Y-series:", error);
        const fieldKey = `y_series_${seriesNumber}`;
        setSelectedAttributes((prev) => {
          const newAttrs = { ...prev };
          delete newAttrs[fieldKey];
          return newAttrs;
        });
      }
    },
    [
      axisPanelAtt,
      dispatch,
      ySeriesCount,
      shouldResetOrderByOnRemoval,
      determineNewOrderBy,
    ]
  );

  const openEditFields = useCallback(
    (
      series,
      attName,
      agg,
      sort_order,
      x_limit,
      date_part,
      y_limit,
      chart_type,
      percent,
      breakdown_index,
      is_pivot,
      e,
      span_on,
      info_text = ""
    ) => {
      setIsModalVisible(true);

      let isSpanDisabled = false;
      let spanDisabledMsg = "";
      let data_source = axisPanelAtt?.data_source;
      let deep_dive_info = null;

      // Set data source based on series type
      if (series === "x_axis") {
        data_source = axisPanelAtt?.x_axis?.data_source;
        deep_dive_info = axisPanelAtt?.x_axis?.deep_dive_info || null;
      } else if (series === "breakdown") {
        if (
          Array.isArray(axisPanelAtt?.breakdown_by) &&
          axisPanelAtt?.breakdown_by?.length > 1
        ) {
          axisPanelAtt?.breakdown_by?.forEach((b, i) => {
            if (i === breakdown_index) {
              data_source = b?.data_source;
              deep_dive_info = b?.deep_dive_info || null;
            }
          });
        }
      } else {
        data_source = axisPanelAtt?.y_axis?.[series]?.data_source;
        deep_dive_info = axisPanelAtt?.y_axis?.[series]?.deep_dive_info || null;
      }

      // Check span disabled conditions
      if (series === "breakdown") {
        if (axisPanelAtt?.x_axis?.span_on) {
          isSpanDisabled = true;
        } else if (
          Array.isArray(axisPanelAtt?.breakdown_by) &&
          axisPanelAtt?.breakdown_by?.length > 1
        ) {
          axisPanelAtt?.breakdown_by?.forEach((b, i) => {
            if (b?.span_on && i !== breakdown_index) {
              isSpanDisabled = true;
            }
          });
        }
      } else if (
        series === "x_axis" &&
        Array.isArray(axisPanelAtt?.breakdown_by) &&
        axisPanelAtt?.breakdown_by?.length
      ) {
        axisPanelAtt?.breakdown_by?.forEach((b) => {
          if (b?.span_on) {
            isSpanDisabled = true;
          }
        });
      }

      if (
        axisPanelAtt?.chart_settings?.show_previous_duration_comparison ||
        axisPanelAtt?.chart_settings?.show_previous_duration_data
      ) {
        isSpanDisabled = true;
        spanDisabledMsg = "Disable data comparison to enable spanning.";
      }

      const data = {
        boolValue: true,
        series,
        attName,
        aggregation: agg,
        x_axis_sort_order: sort_order,
        x_axis_x_limit: x_limit,
        x_axis_date_part: date_part,
        y_axis_y_limit: y_limit,
        chart_type,
        percent,
        breakdown_index,
        is_pivot,
        span_on,
        span_disabled: isSpanDisabled,
        span_disabled_msg: spanDisabledMsg,
        info_text,
        data_source,
        deep_dive_info,
      };

      dispatch(setChartEditField(data, axisPanelAtt));
    },
    [axisPanelAtt, dispatch]
  );

  const onChange = useCallback(
    (key) => {
      if (key === "filters") {
        setfilterSearch("");
        dispatch(setFilterActiveKey([]));
      }
    },
    [dispatch]
  );

  const handleprolificChange = useCallback(
    (value) => {
      setPolificOptions(value);
      const obj = {
        attribute: value,
        offset: 0,
        sort_order: value === "firstPost" ? "asc" : "desc",
      };
      dispatch(setPolificOrderByAttribute(obj, axisPanelAtt));
    },
    [axisPanelAtt, dispatch]
  );

  // Helper function to get field data for edit
  const getFieldEditData = useCallback(
    (fieldType, selectedData) => {
      let aggregation = DEFAULT_AGGREGATION;
      let sort_order = DEFAULT_SORT_ORDER;
      let x_limit = DEFAULT_LIMIT;
      let date_part = null;
      let y_limit = null;
      let percent = false;
      let span_on = false;

      if (fieldType === "x_axis") {
        aggregation = axisPanelAtt?.x_axis?.aggregation || DEFAULT_AGGREGATION;
        sort_order = axisPanelAtt?.x_axis?.sort_order || DEFAULT_SORT_ORDER;
        x_limit = axisPanelAtt?.x_axis?.x_limit || DEFAULT_LIMIT;
        date_part = axisPanelAtt?.x_axis?.date_part || null;
        percent = axisPanelAtt?.x_axis?.percent || false;
        span_on = axisPanelAtt?.x_axis?.span_on || false;
      } else if (fieldType.startsWith("y_series_")) {
        aggregation =
          axisPanelAtt?.y_axis?.[fieldType]?.aggregation || DEFAULT_AGGREGATION;
        y_limit = axisPanelAtt?.y_axis?.[fieldType]?.y_limit || null;
        percent = axisPanelAtt?.y_axis?.[fieldType]?.percent || false;
      } else if (fieldType === "breakdown" || fieldType === "y_axes") {
        if (axisPanelAtt?.breakdown_by?.length > 0) {
          aggregation =
            axisPanelAtt?.breakdown_by[0]?.aggregation || DEFAULT_AGGREGATION;
          sort_order =
            axisPanelAtt?.breakdown_by[0]?.sort_order || DEFAULT_SORT_ORDER;
          x_limit = axisPanelAtt?.breakdown_by[0]?.x_limit || DEFAULT_LIMIT;
          y_limit = axisPanelAtt?.breakdown_by[0]?.y_limit || 5; // Default y_limit for breakdown
          percent = axisPanelAtt?.breakdown_by[0]?.percent || false;
          span_on = axisPanelAtt?.breakdown_by[0]?.span_on || false;
          date_part = axisPanelAtt?.breakdown_by[0]?.date_part || null;
        } else {
          // Set default y_limit even when no breakdown_by exists yet
          y_limit = 5;
        }
      } else if (fieldType === "multilevel_breakdown") {
        // Handle multilevel_breakdown field
        if (axisPanelAtt?.multilevel_breakdown) {
          aggregation =
            axisPanelAtt?.multilevel_breakdown?.aggregation ||
            DEFAULT_AGGREGATION;
          sort_order =
            axisPanelAtt?.multilevel_breakdown?.sort_order ||
            DEFAULT_SORT_ORDER;
          x_limit =
            axisPanelAtt?.multilevel_breakdown?.x_limit || DEFAULT_LIMIT;
          date_part = axisPanelAtt?.multilevel_breakdown?.date_part || null;
          y_limit = axisPanelAtt?.multilevel_breakdown?.y_limit || 5;
          percent = axisPanelAtt?.multilevel_breakdown?.percent || false;
          span_on = axisPanelAtt?.multilevel_breakdown?.span_on || false;
        } else {
          // Set default y_limit even when no multilevel_breakdown exists yet
          y_limit = 5;
        }
      } else if (fieldType.startsWith("y_axes_")) {
        const axesIndex = parseInt(fieldType.split("_")[2]) - 1;
        if (axisPanelAtt?.breakdown_by?.[axesIndex]) {
          aggregation =
            axisPanelAtt?.breakdown_by[axesIndex]?.aggregation ||
            DEFAULT_AGGREGATION;
          sort_order =
            axisPanelAtt?.breakdown_by[axesIndex]?.sort_order ||
            DEFAULT_SORT_ORDER;
          x_limit =
            axisPanelAtt?.breakdown_by[axesIndex]?.x_limit || DEFAULT_LIMIT;
          y_limit = axisPanelAtt?.breakdown_by[axesIndex]?.y_limit || 5; // Default y_limit for breakdown
          percent = axisPanelAtt?.breakdown_by[axesIndex]?.percent || false;
          span_on = axisPanelAtt?.breakdown_by[axesIndex]?.span_on || false;
        } else {
          // Set default y_limit even when no breakdown_by exists yet
          y_limit = 5;
        }
      } else if (fieldType === "filter_by") {
        // Handle filter_by field
        if (axisPanelAtt?.filter_by?.length > 0 && axisPanelAtt.filter_by[0]) {
          sort_order =
            axisPanelAtt?.filter_by[0]?.sort_order || DEFAULT_SORT_ORDER;
          y_limit = axisPanelAtt?.filter_by[0]?.y_limit || 5;
          date_part = axisPanelAtt?.filter_by[0]?.date_part || null;
        } else {
          // Set defaults
          sort_order = "desc";
          y_limit = 5;
        }
      }

      // Map fieldType to the correct series name for the edit modal
      let series = fieldType;
      let is_pivot = false;

      if (fieldType === "multilevel_breakdown") {
        // multilevel_breakdown should use its own series name
        series = "multilevel_breakdown";
      } else if (fieldType === "y_axes" || fieldType.startsWith("y_axes_")) {
        // For breakdown fields (labeled as y_axes), use "breakdown" as series
        series = "breakdown";

        // Check if pivot-grid chart type for is_pivot flag
        if (axisPanelAtt?.chart_type === "pivot-grid") {
          // Get the specific breakdown item to check its is_pivot value
          if (fieldType.startsWith("y_axes_")) {
            const axesIndex = parseInt(fieldType.split("_")[2]) - 1;
            is_pivot =
              axisPanelAtt?.breakdown_by?.[axesIndex]?.is_pivot || true;
          } else if (axisPanelAtt?.breakdown_by?.length > 0) {
            is_pivot = axisPanelAtt?.breakdown_by[0]?.is_pivot || true;
          } else {
            is_pivot = true; // Default to true for pivot-grid
          }
        }
      }

      return {
        series,
        attName: selectedData?.attribute || "",
        aggregation,
        sort_order,
        x_limit,
        date_part,
        y_limit,
        chart_type: axisPanelAtt?.chart_type || "",
        percent,
        breakdown_index: fieldType.startsWith("y_axes_")
          ? parseInt(fieldType.split("_")[2]) - 1
          : 0,
        is_pivot,
        span_on,
      };
    },
    [axisPanelAtt]
  );

  // Render functions
  const renderFieldActions = useCallback(
    (fieldType, selectedData, allowsMultiple) => {
      // Check if this is a y-series field and has filters
      const isYSeriesField = fieldType.startsWith("y_series_");
      const filters = isYSeriesField
        ? axisPanelAtt?.y_axis?.[fieldType]?.filters
        : null;
      const filterCount = filters?.length || 0;

      return (
        <div className="field-actions" onClick={(e) => e.stopPropagation()}>
          {/* Show filter badge for y-series fields with filters */}
          {isYSeriesField && filterCount > 0 && (
            <Tooltip
              placement="leftTop"
              overlayStyle={{
                width: "22rem",
              }}
              color="#3B5175"
              title={
                <>
                  <div
                    className="custom_scroll"
                    style={{
                      maxWidth: "27rem",
                      maxHeight: "23rem",
                      overflow: "auto",
                      scrollBehavior: "smooth",
                    }}
                  >
                    {filters?.map((el) => {
                      return (
                        el?.attribute !== "brandid" &&
                        el?.attribute !== "createddate" && (
                          <div className="mb-2" key={el?.attribute}>
                            <p className="mb-0 text-white">
                              <strong>
                                {el?.attribute === "brandid"
                                  ? "Brands"
                                  : All_WM_attributes &&
                                    All_WM_attributes[el?.attribute]
                                  ? All_WM_attributes[el?.attribute]
                                  : el?.attribute?.toLowerCase() ===
                                    "categoryid"
                                  ? "Category"
                                  : el?.attribute?.toLowerCase() ===
                                    "subcategoryid_aug"
                                  ? "Sub Category"
                                  : el?.attribute?.toLowerCase() ===
                                    "subsubcategoryid"
                                  ? "Sub Sub Category"
                                  : el?.attribute?.replace("Type", "")}
                              </strong>
                            </p>
                            {el?.type === "int" &&
                              !Array.isArray(el?.columns) && (
                                <span className="font-weight-light text-white">
                                  {el?.columns?.from + " - " + el?.columns?.to}
                                </span>
                              )}
                            {el?.attribute === "createddate" ? (
                              <span
                                className="font-weight-light"
                                style={{
                                  color: "#fff",
                                }}
                              >
                                {`${el?.columns?.from} - ${el?.columns?.to}`}
                              </span>
                            ) : (
                              Array.isArray(el?.columns) &&
                              el?.columns?.map((e, index) => {
                                return (
                                  <span
                                    key={index}
                                    className="font-weight-light"
                                    style={{
                                      color:
                                        e?.type === "include"
                                          ? "#5ed66d"
                                          : e?.type === "exclude"
                                          ? "#fb7665"
                                          : "#fff",
                                    }}
                                  >
                                    {(index ? ", " : "") +
                                      (e?.display_name
                                        ? e?.display_name
                                        : e?.name === null
                                        ? "Any"
                                        : e?.name === 0
                                        ? "No"
                                        : e?.name === 1
                                        ? "Yes"
                                        : e?.name)}
                                  </span>
                                );
                              })
                            )}
                          </div>
                        )
                      );
                    })}
                  </div>
                </>
              }
            >
              <Space size="small">
                <Badge
                  style={{
                    backgroundColor: "#185edf",
                    fontSize: "1rem",
                    marginRight: "0.5rem",
                  }}
                  title=""
                  size="small"
                  count={filterCount}
                />
              </Space>
            </Tooltip>
          )}
          {fieldType === "order_by" ? (
            ""
          ) : (
            <Button
              type="text"
              icon={<SettingOutlined />}
              size="small"
              className="field-action-button settings-button"
              onClick={(e) => {
                e.stopPropagation();
                const editData = getFieldEditData(fieldType, selectedData);
                openEditFields(
                  editData.series,
                  editData.attName,
                  editData.aggregation,
                  editData.sort_order,
                  editData.x_limit,
                  editData.date_part,
                  editData.y_limit,
                  editData.chart_type,
                  editData.percent,
                  editData.breakdown_index,
                  editData.is_pivot,
                  e,
                  editData.span_on,
                  ""
                );
              }}
            />
          )}
          <Button
            type="text"
            icon={<CloseOutlined />}
            size="small"
            className="field-action-button remove-button"
            onClick={(e) => {
              e.stopPropagation();
              removeAttribute(
                fieldType,
                allowsMultiple ? selectedData?.[0]?.attribute : null
              );
            }}
          />
        </div>
      );
    },
    [
      getFieldEditData,
      openEditFields,
      removeAttribute,
      axisPanelAtt,
      All_WM_attributes,
    ]
  );

  const renderField = useCallback(
    (field, fieldKey = null, seriesNumber = null) => {
      const fieldType = fieldKey || field.payload_key_name;
      let label = field.field_name;

      // Handle label for numbered fields
      if (seriesNumber && field.field_name.toLowerCase() !== "values") {
        label = `${field.field_name} ${seriesNumber}`;
      }
      if (fieldKey?.startsWith("y_axes_")) {
        const axesNumber = fieldKey.split("_")[2];
        label = `${field.field_name} ${axesNumber}`;
      }
      if (fieldKey?.startsWith("filter_dropdown_")) {
        const filterNumber = fieldKey.split("_")[2];
        label = `${field.field_name} ${filterNumber}`;
      }

      // Check if field is required
      let required = field.is_required;

      // Special handling for Y-series fields:
      // Y-series 1 is always mandatory for all chart types
      if (fieldType === "y_series_1") {
        required = true;
      }

      // Y-series 2 is required ONLY for combination, scatter, and multilayer-map charts
      // NOT required for line, area, bar, etc.
      if (fieldType === "y_series_2") {
        const chartType = axisPanelAtt?.chart_type;
        if (
          chartType === "combination" ||
          chartType === "scatter" ||
          chartType === "multilayer-map"
        ) {
          required = true;
        } else {
          // For line chart and others, Y-series 2 is optional
          required = false;
        }
      }

      // Y-series 3 and beyond are NEVER mandatory, even for combination charts
      if (fieldType && fieldType.startsWith("y_series_")) {
        const seriesNumber = parseInt(fieldType.split("_")[2]);
        if (seriesNumber >= 3) {
          required = false;
        }
      }
      const color = "blue";
      const colorClasses = {
        blue: "drop-zone-blue",
        green: "drop-zone-green",
        purple: "drop-zone-purple",
      };

      // Get field configuration
      const baseFieldType = fieldType.startsWith("y_series_")
        ? "y_series"
        : fieldType.startsWith("filter_dropdown_")
        ? "filter_dropdown"
        : fieldType;

      const fieldConfig =
        chartAttributesFieldConfig?.data?.find(
          (f) => f.payload_key_name === baseFieldType
        ) || field;

      const isValuesField = field.field_name?.toLowerCase() === "values";
      const allowsMultiple =
        fieldType.startsWith("y_series_") ||
        fieldType.startsWith("y_axes_") ||
        fieldType.startsWith("filter_dropdown_") ||
        isValuesField
          ? false
          : fieldConfig?.multiple_allowed || false;

      let selectedData =
        selectedAttributes[fieldType] || (allowsMultiple ? [] : null);

      // Handle special cases
      if (
        fieldType === "filter_dropdown" &&
        Array.isArray(selectedData) &&
        !allowsMultiple &&
        selectedData.length > 0
      ) {
        selectedData = selectedData[0];
      }

      if (
        (fieldType === "breakdown" || fieldType === "y_axes") &&
        !selectedData &&
        axisPanelAtt?.breakdown_by?.length > 0
      ) {
        const firstBreakdown = axisPanelAtt.breakdown_by[0];
        selectedData = createAttributeData(
          firstBreakdown.attribute,
          firstBreakdown.trace_name
        );
      }

      if (
        fieldType === "filter_dropdown" &&
        !selectedData &&
        axisPanelAtt?.filter_dropdown?.length > 0
      ) {
        selectedData = axisPanelAtt.filter_dropdown.map((filterData) =>
          createAttributeData(filterData.attribute, filterData.trace_name)
        );
      }

      const renderRemoveButton = () => {
        // Don't show remove button for required/mandatory fields
        if (required) {
          return null;
        }

        if (
          fieldType.startsWith("y_series_") &&
          parseInt(fieldType.split("_")[2]) > 1
        ) {
          return (
            <Tooltip title={`Remove ${label}`}>
              <Button
                type="link"
                icon={<MinusCircleOutlined />}
                size="small"
                className="remove-series-text-inline"
                onClick={() => removeYSeries(parseInt(fieldType.split("_")[2]))}
              />
            </Tooltip>
          );
        }
        if (
          fieldType.startsWith("y_axes_") &&
          parseInt(fieldType.split("_")[2]) > 1
        ) {
          return (
            <Tooltip title={`Remove ${label}`}>
              <Button
                type="link"
                icon={<MinusCircleOutlined />}
                size="small"
                className="remove-series-text-inline"
                onClick={() => removeYAxes(parseInt(fieldType.split("_")[2]))}
              />
            </Tooltip>
          );
        }
        if (
          fieldType.startsWith("filter_dropdown_") &&
          parseInt(fieldType.split("_")[2]) > 1
        ) {
          return (
            <Tooltip title={`Remove ${label}`}>
              <Button
                type="link"
                icon={<MinusCircleOutlined />}
                size="small"
                className="remove-series-text-inline"
                onClick={() =>
                  removeQuickFilter(parseInt(fieldType.split("_")[2]))
                }
              />
            </Tooltip>
          );
        }
        return null;
      };

      return (
        <div key={fieldType} className="field-section">
          <div className="field-container">
            <div className="field-label-container">
              <Text
                strong
                className={`field-label ${
                  currentField === fieldType ? "current-field" : ""
                }`}
              >
                {label}{" "}
                {required && <span className="required-asterisk">*</span>}
                {field?.info_text ? (
                  <Tooltip placement="top" title={field?.info_text}>
                    <img
                      src={informationButton}
                      alt="Info"
                      className="cursor-pointer ml-2"
                      style={{ maxWidth: "1.6rem" }}
                    />
                  </Tooltip>
                ) : (
                  ""
                )}
              </Text>
              {renderRemoveButton()}
            </div>
            <div
              className={`drop-zone ${colorClasses[color]} ${
                currentField === fieldType ? "current-field" : ""
              } ${
                newlyAddedField === fieldType ? "newly-added-highlight" : ""
              }`}
            >
              {allowsMultiple ? (
                selectedData.length > 0 ? (
                  <div
                    className="selected-field-content"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAttributeModal(fieldType);
                    }}
                  >
                    <Tooltip
                      title={
                        selectedData.length === 1
                          ? selectedData[0].title
                          : selectedData.map((attr) => attr.title).join(", ")
                      }
                    >
                      <Text className="selected-field-text" ellipsis>
                        {selectedData[0].title}
                        {selectedData.length > 1 &&
                          ` (+${selectedData.length - 1})`}
                      </Text>
                    </Tooltip>
                    {renderFieldActions(
                      fieldType,
                      selectedData,
                      allowsMultiple
                    )}
                  </div>
                ) : (
                  <div
                    className="empty-drop-zone"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAttributeModal(fieldType);
                    }}
                  >
                    <Text type="secondary" className="drop-zone-placeholder">
                      Click to select attribute
                    </Text>
                  </div>
                )
              ) : selectedData ? (
                <div
                  className="selected-field-content"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAttributeModal(fieldType);
                  }}
                >
                  <Tooltip title={selectedData.title}>
                    <Text className="selected-field-text" ellipsis>
                      {selectedData.title}
                    </Text>
                  </Tooltip>
                  {renderFieldActions(fieldType, selectedData, allowsMultiple)}
                </div>
              ) : (
                <div
                  className="empty-drop-zone"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openAttributeModal(fieldType);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Text type="secondary">Click to select attribute</Text>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    [
      selectedAttributes,
      axisPanelAtt,
      currentField,
      newlyAddedField,
      createAttributeData,
      openAttributeModal,
      renderFieldActions,
      removeYSeries,
      removeYAxes,
      removeQuickFilter,
    ]
  );

  const renderMultipleFields = useCallback(
    (
      field,
      baseFieldName,
      count,
      renderFunc,
      addFunc,
      limit,
      addButtonText
    ) => {
      // Special handling for cluster-chart and grid type to allow multiple y_axes
      const isClusterChart = axisPanelAtt?.chart_type === "cluster-chart";
      const isGridChart = axisPanelAtt?.chart_type === "grid";
      const isSankeyChart = axisPanelAtt?.chart_type === "sankey";
      const allowMultiple =
        baseFieldName === "y_axes" &&
        (isClusterChart || isGridChart || isSankeyChart)
          ? true
          : field.multiple_allowed;

      // Don't show "Add another Y-series" button for scatter and multilayer-map charts
      const isScatterOrMultiLayer =
        axisPanelAtt?.chart_type === "scatter" ||
        axisPanelAtt?.chart_type === "multilayer-map";
      const hideAddButtonForYSeries =
        baseFieldName === "y_series" && isScatterOrMultiLayer;

      const showAddButton =
        allowMultiple &&
        count < limit &&
        selectedAttributes[`${baseFieldName}_${count}`] &&
        !hideAddButtonForYSeries;

      return (
        <div key={`${baseFieldName}-container`} className="y-series-container">
          {Array.from({ length: count }, (_, index) => {
            const number = index + 1;
            const fieldKey = `${baseFieldName}_${number}`;
            return renderFunc(field, fieldKey, number);
          })}
          {showAddButton && (
            <Button
              type="link"
              icon={<span style={{ marginRight: 4 }}>+</span>}
              onClick={addFunc}
              className="add-y-series-btn"
            >
              {addButtonText}
            </Button>
          )}
        </div>
      );
    },
    [selectedAttributes, axisPanelAtt?.chart_type]
  );

  // Main render
  const isDarkTheme = authParams?.theme_type === "1";
  const themeClass = isDarkTheme ? "darkTheme" : "lightTheme";

  return (
    <div
      className={`widget-maker-attribute-field-config-unique chart-configuration ${themeClass}`}
    >
      {axisPanelAtt?.chart_type === "prolific-user" && (
        <Select
          defaultValue={polificOptions}
          className="w-100"
          onChange={handleprolificChange}
          dropdownClassName={dropdownClassName}
        >
          <Option value="engagement">Most Engaged Post</Option>
          <Option value="mostPost">Most Recent post</Option>
          <Option value="firstPost">First Post</Option>
        </Select>
      )}

      <Card className={`main-card ${themeClass}`}>
        <div className="fields-container">
          {graphInfoLoader ? (
            <div className="skeleton-container">
              {/* X-axis skeleton */}
              <div className="field-section">
                <div className="field-container">
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: "80px", marginBottom: "8px" }}
                  />
                  <div
                    style={{
                      border: isDarkTheme
                        ? "1px solid #333"
                        : "1px solid #d9d9d9",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: isDarkTheme ? "#2a2a2a" : "transparent",
                    }}
                  >
                    <Skeleton.Input
                      active
                      size="default"
                      style={{ width: "200px" }}
                    />
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Skeleton.Button active size="small" shape="circle" />
                      <Skeleton.Button active size="small" shape="circle" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Y-series skeleton */}
              <div className="field-section">
                <div className="field-container">
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: "100px", marginBottom: "8px" }}
                  />
                  <div
                    style={{
                      border: isDarkTheme
                        ? "1px solid #333"
                        : "1px solid #d9d9d9",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: isDarkTheme ? "#2a2a2a" : "transparent",
                    }}
                  >
                    <Skeleton.Input
                      active
                      size="default"
                      style={{ width: "200px" }}
                    />
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Skeleton.Button active size="small" shape="circle" />
                      <Skeleton.Button active size="small" shape="circle" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            sortedFields.map((field) => {
              const baseFieldName = field.payload_key_name;
              const isValuesField =
                field.field_name?.toLowerCase() === "values";
              const isYSeriesField = baseFieldName === "y_series";
              const isMeasureField =
                field.field_name?.toLowerCase().includes("measure") ||
                field.field_name?.toLowerCase().includes("numeric");

              // Handle y_series/values fields
              if (isYSeriesField || isValuesField || isMeasureField) {
                return renderMultipleFields(
                  field,
                  "y_series",
                  ySeriesCount,
                  renderField,
                  addYSeries,
                  ySeriesLimit,
                  `Add another ${field.field_name}`
                );
              }

              // Handle y_axes fields
              const isClusterChart =
                axisPanelAtt?.chart_type === "cluster-chart";
              const isGridChart = axisPanelAtt?.chart_type === "grid";
              const isSankeyChart = axisPanelAtt?.chart_type === "sankey";
              const allowMultipleYAxes =
                isClusterChart ||
                isGridChart ||
                isSankeyChart ||
                field.multiple_allowed;
              if (baseFieldName === "y_axes" && allowMultipleYAxes) {
                return renderMultipleFields(
                  field,
                  "y_axes",
                  yAxesCount,
                  renderField,
                  addYAxes,
                  yAxesLimit,
                  `Add another ${field.field_name}`
                );
              }

              // Handle filter_dropdown fields
              if (
                baseFieldName === "filter_dropdown" &&
                field.multiple_allowed
              ) {
                return (
                  <div
                    key="filter_dropdown-container"
                    className="y-series-container"
                  >
                    {Array.from(
                      { length: Math.max(1, quickFilterCount) },
                      (_, index) => {
                        const filterNumber = index + 1;
                        const fieldKey = `filter_dropdown_${filterNumber}`;
                        return renderField(field, fieldKey, filterNumber);
                      }
                    )}
                    {Math.max(1, quickFilterCount) <
                      CHART_LIMITS.QUICK_FILTER &&
                      selectedAttributes[
                        `filter_dropdown_${Math.max(1, quickFilterCount)}`
                      ] && (
                        <Button
                          type="link"
                          icon={<span style={{ marginRight: 4 }}>+</span>}
                          onClick={addQuickFilter}
                          className="add-y-series-btn"
                        >
                          Add another Quick Filter
                        </Button>
                      )}
                  </div>
                );
              }

              // Render single fields
              return renderField(field);
            })
          )}
        </div>
      </Card>

      <EditFieldModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        editName={editName}
        setEditName={setEditName}
        editAggregation={editAggregation}
        setAggregation={setAggregation}
        filterSearch={filterSearch}
        setfilterSearch={setfilterSearch}
        isFilterApplied={isFilterApplied}
        setisFilterApplied={setisFilterApplied}
        widgetMakerClearFilter={widgetMakerClearFilter}
        setWidgetMakerClearFilter={setWidgetMakerClearFilter}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onChange={onChange}
      />
    </div>
  );
};

export default React.memo(AttributeFieldConfig);
